import styled from '@emotion/styled';
import { Heading2, Text, CheckBox, Modal, FieldMessage } from '@blueprint/ui';
import { css } from '@emotion/react';
import { getRems } from '@blueprint/utils';
import { AuthModalProps } from './AuthModal.types';
import { Heading1 } from '@blueprint/heading';
import { StyledTabs } from '@zoopla/shared/components';
import { ButtonWithLoading } from 'shared/components/ButtonWithLoading';
import { StyledButtonWithLoading } from '../ButtonWithLoading/ButtonWithLoading.styles';

const TITLEBAR_HEIGHT = '3.5rem';

const StyledModal = styled(Modal)<Partial<AuthModalProps>>(
    ({ theme: { mediaQueries, spacing } }) => css`
        ${mediaQueries.small} {
            min-height: 90vh;
        }

        ${StyledTabs} {
            margin: 0 ${spacing['16px']};

            ${mediaQueries.small} {
                margin: 0 ${spacing['48px']};
            }
        }
    `
);

const AuthModalHeading = styled(Heading2)(
    ({ theme: { mediaQueries, spacing } }) => css`
        text-align: center;
        margin: 0 ${spacing['24px']} ${spacing['48px']} ${spacing['24px']};
        ${mediaQueries.small} {
            margin: 0 ${spacing['80px']} ${spacing['24px']} ${spacing['80px']};
        }
    `
);

const AuthModalSubHeadingWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const FieldContainer = styled('div')``;

const RequiredContainer = styled('div')`
    float: right;
`;

const RequiredIndicator = styled('span')(({ theme: { colors } }) => {
    return css`
        color: ${colors['function-error']};
    `;
});

const AuthContextText = styled(Text)(
    ({ theme: { spacing, mediaQueries } }) => css`
        text-align: center;
        margin: ${spacing['24px']} ${spacing['48px']};
        ${mediaQueries.small} {
            margin: ${spacing['24px']} ${spacing['80px']};
        }

        > a {
            margin-left: ${spacing['8px']};
        }
    `
);

const FormSection = styled('div')<Partial<AuthModalProps>>(
    ({ theme: { spacing } }) => css`
        margin-bottom: ${spacing['32px']};

        &:first-of-type {
            margin-top: ${spacing['24px']} ${spacing['32px']};
        }

        > *:not(:last-child) {
            margin-bottom: ${spacing['16px']};
        }
    `
);

const AboutYouLabel = styled('label')(
    (props) => css`
        font-size: ${props.theme.type.secondary};
        line-height: ${props.theme.type.secondary};
        color: ${props.theme.colors['text-body']};
        display: block;
        margin-bottom: 0;
    `
);

const AuthModalCheckbox = styled(CheckBox)(
    ({ theme: { spacing } }) => css`
        margin-top: ${spacing['16px']};
        margin-bottom: ${spacing['16px']};
    `
);

const ReassuranceText = styled(Text)(
    ({ theme: { spacing, mediaQueries } }) => css`
        margin-bottom: ${spacing['32px']};
        text-align: center;

        ${mediaQueries.medium} {
            text-align: left;
        }
    `
);

const TermsText = styled(Text)(`
        text-align: center;
`);

const RegisterButton = styled(ButtonWithLoading)(
    ({ theme: { spacing } }) => css`
        margin-bottom: ${spacing['24px']};

        /** fixed height for button with bigger icons, fix for Safari (Mac) **/
        height: ${getRems(44.15)};
        padding-top: 0;
        padding-bottom: 0;
    `
);

const SignInButton = styled(ButtonWithLoading)(
    ({ theme: { spacing } }) => css`
        margin-bottom: ${spacing['24px']};

        /** fixed height for button with bigger icons, fix for Safari (Mac) **/
        height: ${getRems(44.15)};
        padding-top: 0;
        padding-bottom: 0;
    `
);

// TODO: alias box-shadow to button-hover
const SocialSignInWrapper = styled('div')(
    ({ theme: { boxShadow, spacing, colors } }) => css`
        margin-bottom: ${spacing['32px']};

        ${StyledButtonWithLoading} {
            position: relative;
            color: ${colors['neutral-6']};
            border-color: ${colors['neutral-4']};

            /** fixed height for button with bigger icons, fix for Safari (Mac) **/
            height: ${getRems(44.15)};
            padding-top: 0;
            padding-bottom: 0;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: block;
                width: 100%;
                height: 100%;
                border-radius: inherit;
            }

            &:hover,
            &:focus {
                color: ${colors['neutral-6']};
                border-color: ${colors['neutral-4']};
                background-color: ${colors['graphic-background']};
            }

            &:hover {
                &:before {
                    box-shadow: ${boxShadow['200']};
                }
            }
        }

        ${StyledButtonWithLoading} + ${StyledButtonWithLoading} {
            margin-top: ${spacing['24px']};
        }
    `
);

const SocialSignInText = styled(Text)(
    ({ theme: { spacing } }) => css`
        text-align: center;
        margin-bottom: ${spacing['24px']};
    `
);

const SignInErrorMessage = styled(FieldMessage)(
    ({ theme: { spacing, type } }) => css`
        line-height: ${type.lineHeight};
        margin-bottom: ${spacing['16px']};
    `
);

const SocialTitleWrapper = styled('div')(
    ({ theme: { spacing, colors, mediaQueries, focusRing } }) => css`
        position: relative;
        margin-bottom: ${spacing['32px']};

        &:before {
            content: '';
            display: block;
            position: absolute;
            left: 0;
            top: 0;
            background: ${colors['primary-brand']};
            width: ${getRems(10)};
            top: -${TITLEBAR_HEIGHT};
            height: calc(100% + ${TITLEBAR_HEIGHT});
            z-index: 1000;
        }

        ${Heading1} {
            display: block;
            margin-left: ${spacing['32px']};

            &:focus {
                outline: none;
            }

            &:focus-visible {
                outline: none;
                box-shadow: ${focusRing};
            }

            ${mediaQueries.small} {
                padding-right: ${spacing['64px']};
            }
        }
    `
);

export {
    StyledModal,
    AuthModalHeading,
    AuthModalSubHeadingWrapper,
    RequiredContainer,
    RequiredIndicator,
    AuthContextText,
    FormSection,
    FieldContainer,
    AboutYouLabel,
    AuthModalCheckbox,
    ReassuranceText,
    TermsText,
    RegisterButton,
    SocialSignInWrapper,
    SocialSignInText,
    SignInButton,
    SignInErrorMessage,
    SocialTitleWrapper
};
